import { timezoneToCountryCode } from './timezoneToCountry';
import { getTimezoneInfo, normalizeTimezone } from './timezone';

export type TZOption = {
  value: string;
  label: string;
};

export type CountryTZ = {
  code: string;
  name: string;
  timezones: TZOption[];
};

function getCityLabel(tzValue: string): string {
  const parts = tzValue.split('/');
  return parts[parts.length - 1].replace(/_/g, ' ');
}

function offsetToMinutes(offset: string): number {
  const m = offset.match(/UTC([+-])(\d{2}):(\d{2})/);
  if (!m) return 0;
  return (m[1] === '+' ? 1 : -1) * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
}

function build(): CountryTZ[] {
  const allTZ: string[] =
    (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
      .supportedValuesOf?.('timeZone') ?? [];

  const displayNames = new Intl.DisplayNames(['es'], { type: 'region' });
  const grouped = new Map<string, Set<string>>();

  for (const rawTz of allTZ) {
    const code = timezoneToCountryCode(rawTz);
    if (!code || code === 'ZZ') continue;
    // Las 12 zonas argentinas normalizan todas al mismo valor (mismo huso en todo el país):
    // el Set las deduplica y Argentina queda con una sola opción, Buenos Aires.
    const tz = normalizeTimezone(rawTz);
    if (!grouped.has(code)) grouped.set(code, new Set());
    grouped.get(code)?.add(tz);
  }

  const result: CountryTZ[] = [];
  for (const [code, tzs] of Array.from(grouped.entries())) {
    const name = displayNames.of(code) ?? code;
    const withOffsets: Array<{ value: string; label: string; mins: number }> = Array.from(tzs).map((tz: string) => {
      const info = getTimezoneInfo(tz);
      return {
        value: tz,
        label: `${getCityLabel(tz)} (${info.offset})`,
        mins: offsetToMinutes(info.offset),
      };
    });
    withOffsets.sort((a: { mins: number }, b: { mins: number }) => a.mins - b.mins);
    result.push({
      code,
      name,
      timezones: withOffsets.map(({ value, label }: { value: string; label: string }) => ({ value, label })),
    });
  }

  result.sort((a, b) => a.name.localeCompare(b.name, 'es'));

  result.push({
    code: 'UTC',
    name: 'UTC',
    timezones: [{ value: 'UTC', label: 'UTC (UTC+00:00)' }],
  });

  return result;
}

export const COUNTRY_TIMEZONES: CountryTZ[] = build();

export function findCountryByTimezone(tz: string): CountryTZ | null {
  return COUNTRY_TIMEZONES.find(c => c.timezones.some(t => t.value === tz)) ?? null;
}

export type TimezoneDisplayInfo = {
  countryCode: string | null;
  label: string;   // "Argentina / Buenos Aires"
  offset: string;  // "UTC-03:00"
  flagUrl: string | null;
};

export function getTimezoneDisplayInfo(rawTz: string): TimezoneDisplayInfo {
  // Normalizado a la entrada: los perfiles que ya tienen guardada otra ciudad argentina
  // (ej. "America/Argentina/Cordoba") se muestran igual como Buenos Aires.
  const tz = normalizeTimezone(rawTz);
  const { offset } = getTimezoneInfo(tz);
  const countryData = findCountryByTimezone(tz);
  const countryCode = countryData?.code ?? (tz !== 'UTC' ? timezoneToCountryCode(tz) : null);
  const countryName = countryData?.name
    ?? (countryCode ? new Intl.DisplayNames(['es'], { type: 'region' }).of(countryCode) ?? null : null);
  const cityLabel = tz !== 'UTC' ? (tz.split('/').pop()?.replace(/_/g, ' ') ?? '') : '';
  const label = countryName ? `${countryName}${cityLabel ? ` / ${cityLabel}` : ''}` : cityLabel || tz;
  const flagUrl = countryCode && countryCode !== 'UTC'
    ? `https://flagcdn.com/${countryCode.toLowerCase()}.svg`
    : null;
  return { countryCode: countryCode ?? null, label, offset, flagUrl };
}

// Precarga todas las banderas en background cuando el módulo se importa
if (typeof window !== 'undefined') {
  COUNTRY_TIMEZONES.forEach(c => {
    if (c.code === 'UTC') return;
    const img = new window.Image();
    img.src = `https://flagcdn.com/${c.code.toLowerCase()}.svg`;
  });
}
